import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import {
  ArrowRight,
  AudioLines,
  Calendar,
  Crown,
  Phone,
  PhoneMissed,
  User,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { AnalyticsTab } from '@/components/dashboard/AnalyticsTab'
import { useAnalyticsDrilldown } from '@/api/analyticsDrilldown'
import { useProfile } from '@/api/profile'
import { useUIStore } from '@/stores/useUIStore'
import { InlineRecordingPlayer } from '@/components/manager/history/InlineRecordingPlayer'
import { formatDateTime, formatDuration } from './drilldown/formatters'
import type {
  DrilldownCallRow,
} from '@/types/analyticsDrilldown'
import type {
  AgentPerformanceRow,
  TeamTotals,
} from '@/types/teamPerformance'

interface Props {
  agent: AgentPerformanceRow
  teamTotals: TeamTotals | null
  period: string
}

/**
 * Contenu détaillé pour un agent — rendu en pleine largeur dans AgentAnalyticsPage.
 *
 * Composé de :
 *  1. Header agent (avatar + nom + role)
 *  2. Bandeau comparaison vs équipe (3 KPIs)
 *  3. 3 raccourcis drilldown (Tous appels / Manqués / RDV)
 *  4. 5 derniers appels avec audio inline (coaching)
 *  5. AnalyticsTab embedded (avec agentId, hideHeader)
 */
export function AgentDrillContent({ agent, teamTotals, period }: Props) {
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const darkMode = useUIStore((s) => s.darkMode)
  const { data: profile } = useProfile()
  const companyId = profile?.company

  return (
    <div className="space-y-5">
      <AgentHeader agent={agent} darkMode={darkMode} />
      {teamTotals && (
        <TeamComparison agent={agent} teamTotals={teamTotals} darkMode={darkMode} />
      )}
      <DrilldownShortcuts agent={agent} period={period} locale={locale} darkMode={darkMode} />
      {companyId && (
        <RecentCallsSection
          agentId={agent.user_id}
          companyId={companyId}
          period={period}
          locale={locale}
          darkMode={darkMode}
        />
      )}
      <div className={cn('rounded-xl border overflow-hidden', darkMode ? 'border-slate-700' : 'border-slate-200')}>
        <AnalyticsTab agentId={agent.user_id} hideHeader />
      </div>
    </div>
  )
}

// ───── Header agent ─────

function AgentHeader({
  agent,
  darkMode,
}: {
  agent: AgentPerformanceRow
  darkMode: boolean
}) {
  const { t } = useTranslation()
  const isManager = agent.role === 'MANAGER'
  const initials = agent.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('')

  return (
    <div className="flex items-center gap-4">
      <div
        className={cn(
          'h-14 w-14 rounded-full flex items-center justify-center font-semibold text-lg flex-shrink-0',
          isManager
            ? darkMode
              ? 'bg-amber-500/20 text-amber-300'
              : 'bg-amber-100 text-amber-700'
            : darkMode
              ? 'bg-slate-700 text-slate-200'
              : 'bg-slate-200 text-slate-700',
        )}
      >
        {agent.avatar_url ? (
          <img
            src={agent.avatar_url}
            alt=""
            className="h-full w-full rounded-full object-cover"
          />
        ) : (
          initials || <User className="h-6 w-6" />
        )}
      </div>
      <div className="flex flex-col min-w-0">
        <h1
          className={cn(
            'text-2xl font-semibold truncate flex items-center gap-2',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {agent.name}
          {isManager && (
            <Crown
              className="h-5 w-5 text-amber-500 flex-shrink-0"
              aria-label={t('manager.analytics.table.managerBadge')}
            />
          )}
        </h1>
        <span
          className={cn(
            'text-xs uppercase tracking-wide font-medium',
            darkMode ? 'text-slate-500' : 'text-slate-400',
          )}
        >
          {agent.role}
        </span>
      </div>
    </div>
  )
}

// ───── Comparaison vs équipe ─────

interface RowMetric {
  key: keyof TeamTotals
  format: (v: number) => string
  negativeWhenHigher?: boolean
}

const COMPARISON_METRICS: RowMetric[] = [
  { key: 'answer_rate_pct', format: (v) => `${v.toFixed(1)}%` },
  { key: 'missed_total', format: (v) => `${v}`, negativeWhenHigher: true },
  { key: 'talk_time_seconds', format: (v) => formatDuration(v) },
]

function TeamComparison({
  agent,
  teamTotals,
  darkMode,
}: {
  agent: AgentPerformanceRow
  teamTotals: TeamTotals
  darkMode: boolean
}) {
  const { t } = useTranslation()
  const cardBg = darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'
  const labelColor = darkMode ? 'text-slate-400' : 'text-slate-500'
  const valueColor = darkMode ? 'text-white' : 'text-slate-900'

  return (
    <div className={cn('rounded-xl border p-5', cardBg)}>
      <h3 className={cn('text-xs font-semibold uppercase tracking-wide mb-4', labelColor)}>
        {t('manager.analytics.agentPanel.comparisonTitle')}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {COMPARISON_METRICS.map((m) => {
          const agentValueRaw = agentValueOf(agent, m.key)
          const teamValueRaw = teamTotals[m.key] as number
          const diff = agentValueRaw - teamValueRaw
          const positiveDelta = m.negativeWhenHigher ? diff < 0 : diff > 0

          return (
            <div key={m.key} className="flex flex-col gap-1">
              <span className={cn('text-xs', labelColor)}>
                {t(`manager.analytics.cards.${m.key}`)}
              </span>
              <div className="flex items-baseline gap-2">
                <span className={cn('text-2xl font-semibold tabular-nums', valueColor)}>
                  {m.format(agentValueRaw)}
                </span>
                {diff !== 0 && (
                  <span
                    className={cn(
                      'text-xs font-medium tabular-nums',
                      positiveDelta
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-rose-600 dark:text-rose-400',
                    )}
                  >
                    {diff > 0 ? '+' : ''}
                    {m.format(diff)}
                  </span>
                )}
              </div>
              <span className={cn('text-xs tabular-nums', labelColor)}>
                {t('manager.analytics.agentPanel.vsTeam', {
                  value: m.format(teamValueRaw),
                })}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function agentValueOf(agent: AgentPerformanceRow, key: keyof TeamTotals): number {
  switch (key) {
    case 'calls_total':
      return agent.calls_total
    case 'calls_inbound':
      return agent.calls_inbound_answered
    case 'calls_outbound':
      return agent.calls_outbound
    case 'missed_total':
      return agent.calls_missed_rung
    case 'answer_rate_pct':
      return agent.answer_rate_pct
    case 'talk_time_seconds':
      return agent.talk_time_seconds
    case 'sms_sent':
      return agent.sms_sent
    case 'appointments_created':
      return agent.appointments_created
    case 'voicemails_listened':
      return agent.voicemails_listened
    default:
      return 0
  }
}

// ───── Raccourcis drilldown ─────

const SHORTCUTS = [
  { metric: 'calls_total', icon: Phone, labelKey: 'manager.analytics.agentPanel.shortcuts.allCalls' },
  { metric: 'missed', icon: PhoneMissed, labelKey: 'manager.analytics.agentPanel.shortcuts.missed' },
  { metric: 'appointments_created', icon: Calendar, labelKey: 'manager.analytics.agentPanel.shortcuts.appointments' },
] as const

function DrilldownShortcuts({
  agent,
  period,
  locale,
  darkMode,
}: {
  agent: AgentPerformanceRow
  period: string
  locale: string
  darkMode: boolean
}) {
  const { t } = useTranslation()
  const btnClass = cn(
    'inline-flex items-center justify-between gap-2 px-4 py-3 rounded-lg border text-sm font-medium',
    'transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
    darkMode
      ? 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700'
      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-indigo-300',
  )

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {SHORTCUTS.map(({ metric, icon: Icon, labelKey }) => (
        <Link
          key={metric}
          to={`/${locale}/manager/analytics/drilldown?metric=${metric}&user_id=${agent.user_id}&period=${period}`}
          className={btnClass}
        >
          <span className="inline-flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            {t(labelKey)}
          </span>
          <ArrowRight className="h-3.5 w-3.5 opacity-60" />
        </Link>
      ))}
    </div>
  )
}

// ───── 5 derniers appels avec audio ─────

function RecentCallsSection({
  agentId,
  companyId,
  period,
  locale,
  darkMode,
}: {
  agentId: number
  companyId: number
  period: string
  locale: string
  darkMode: boolean
}) {
  const { t } = useTranslation()
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const { data, isLoading } = useAnalyticsDrilldown(companyId, {
    metric: 'calls_total',
    user_id: agentId,
    period,
    page: 1,
    page_size: 5,
  })

  const cardBg = darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'
  const labelColor = darkMode ? 'text-slate-400' : 'text-slate-500'

  return (
    <div className={cn('rounded-xl border p-5', cardBg)}>
      <h3 className={cn('text-xs font-semibold uppercase tracking-wide mb-3', labelColor)}>
        {t('manager.analytics.agentPanel.recentCallsTitle')}
      </h3>
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className={cn('h-10 rounded animate-pulse', darkMode ? 'bg-slate-700' : 'bg-slate-200')}
            />
          ))}
        </div>
      ) : !data || data.results.length === 0 ? (
        <p className={cn('text-sm italic', labelColor)}>
          {t('manager.analytics.agentPanel.recentCallsEmpty')}
        </p>
      ) : (
        <ul className="space-y-2">
          {(data.results as DrilldownCallRow[]).map((call) => {
            const expanded = expandedId === call.id
            // Never a technical identity (client:agent_…): a colleague comes
            // with its name in `contact`; a missing number reads as unknown.
            const peer = call.peer_number?.startsWith('client:') ? '' : call.peer_number
            const peerName = call.contact?.name || peer || t('phone.unknownNumber')
            return (
              <li key={call.id} className="text-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className={cn('truncate font-medium', darkMode ? 'text-white' : 'text-slate-900')}>
                      {peerName}
                    </span>
                    <span className={cn('text-xs', labelColor)}>
                      {formatDateTime(call.date, locale)} · {formatDuration(call.duration_seconds)}
                    </span>
                  </div>
                  {call.has_recordings && (
                    <button
                      type="button"
                      onClick={() => setExpandedId(expanded ? null : call.id)}
                      title={t('manager.analytics.drilldown.calls.playRecording')}
                      className={cn(
                        'inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors flex-shrink-0',
                        expanded
                          ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30'
                          : darkMode
                            ? 'bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
                            : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100',
                      )}
                    >
                      <AudioLines className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {expanded && (
                  <div className="mt-2 flex items-center">
                    <InlineRecordingPlayer
                      companyId={companyId}
                      callId={call.id}
                      onClose={() => setExpandedId(null)}
                    />
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
